import { useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { MdDragIndicator, MdRemoveCircleOutline } from "react-icons/md";
import AssetRow from "./AssetRow";
import useLeagueAssetGate from "./useLeagueAssetGate";
import { cleanSymbol, shortName, formatPrice, formatDayChange, dayChangeColor, ZERO } from "./watchlistFormat";
import ListSkeleton from "../Skeleton/ListSkeleton";

// Edit-mode row (personal lists only): grip handle + chip + name + dimmed
// metrics + remove. Draggable via framer-motion.
function EditRow({ item, onRemove, onDragCommit }) {
  const controls = useDragControls();
  const price = formatPrice(item?.details?.latestData?.c);
  const raw = item?.details?.percentageChange;
  const change = formatDayChange(raw);
  return (
    <Reorder.Item
      as="div"
      value={item}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragCommit}
      className="flex items-center gap-3 border-b border-gray-100 bg-white px-2 py-3"
    >
      <span
        onPointerDown={(e) => controls.start(e)}
        className="shrink-0 cursor-grab touch-none text-tertiary"
        aria-label="Drag to reorder"
      >
        <MdDragIndicator size={20} />
      </span>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primaryColor">
        <span className="truncate px-0.5 text-[10px] font-semibold leading-tight text-white">
          {cleanSymbol(item.symbol)}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-montserrat text-[15px] text-black">
          {shortName(item?.details?.stock_name)}
        </div>
      </div>
      <div className="w-28 shrink-0 text-right text-sm font-semibold text-black opacity-40">
        {price ?? <span className="text-tertiary">—</span>}
      </div>
      <div
        className="w-24 shrink-0 text-right text-sm font-semibold opacity-40"
        style={{ color: change ? dayChangeColor(raw) : ZERO }}
      >
        {change ?? "—"}
      </div>
      <div className="flex w-8 shrink-0 justify-end">
        <button
          type="button"
          aria-label="Remove"
          onClick={() => onRemove?.(item)}
          className="text-red-500 transition-colors hover:text-red-600"
        >
          <MdRemoveCircleOutline size={24} />
        </button>
      </div>
    </Reorder.Item>
  );
}

// Asset table: header (ASSET | PRICE | DAY CHANGE) + rows. View mode uses
// infinite scroll; edit mode uses drag-to-reorder (persisted on drop).
export default function AssetTable({
  items,
  variant,
  editMode,
  onRemove,
  onReorder,
  initialLoading,
  loadingMore,
  hasMore,
  onLoadMore,
}) {
  // Where a row opens, and whether it may. Null-op on the main site; inside a
  // league it routes to the league's Trade screen and refuses assets the league
  // does not allow. Resolved once here rather than once per row.
  const gate = useLeagueAssetGate();

  const loaderRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  // Local order for drag; kept in sync with the incoming items.
  const [order, setOrder] = useState(items);
  const orderRef = useRef(items);
  useEffect(() => {
    setOrder(items);
    orderRef.current = items;
  }, [items]);

  useEffect(() => {
    if (!loaderRef.current || !hasMore || editMode) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMoreRef.current?.();
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, editMode, items.length]);

  if (initialLoading) {
    // Real column header + row skeletons in the same rhythm as the live rows,
    // so the table doesn't shift when prices arrive. No price or day-change
    // placeholder text: a "0.00%" here would read as a flat market.
    return (
      <div aria-busy="true" aria-label="Watchlist loading">
        <div className="flex items-center gap-4 border-b border-gray-100 px-2 pb-3">
          <div className="flex-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-tertiary">
            Asset
          </div>
          <div className="w-28 shrink-0 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-tertiary">
            Price
          </div>
          <div className="w-24 shrink-0 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-tertiary">
            Day Change
          </div>
          {editMode && <div className="w-8 shrink-0" />}
        </div>
        <ListSkeleton
          rows={7}
          avatarSize={36}
          columns={2}
          columnWidths={[76, 60]}
          divided={false}
          rowClassName="px-2 py-3.5 border-b border-divider"
        />
      </div>
    );
  }

  if (!items || items.length === 0) {
    return <div className="py-16 text-center text-sm text-secondary">No assets yet</div>;
  }

  return (
    <div>
      {/* Column header */}
      <div className="flex items-center gap-4 border-b border-gray-100 px-2 pb-3">
        <div className="flex-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-tertiary">Asset</div>
        <div className="w-28 shrink-0 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-tertiary">
          Price
        </div>
        <div className="w-24 shrink-0 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-tertiary">
          Day Change
        </div>
        {editMode && <div className="w-8 shrink-0" />}
      </div>

      {/* Rows */}
      {editMode ? (
        <Reorder.Group
          axis="y"
          values={order}
          onReorder={(newOrder) => {
            setOrder(newOrder);
            orderRef.current = newOrder;
          }}
          as="div"
        >
          {order.map((item) => (
            <EditRow
              key={item.id}
              item={item}
              onRemove={onRemove}
              onDragCommit={() => onReorder?.(orderRef.current)}
            />
          ))}
        </Reorder.Group>
      ) : (
        <div className="divide-y divide-gray-100">
          {items.map((item) => (
            <AssetRow
              key={item.id}
              item={item}
              variant={variant}
              editMode={false}
              onRemove={onRemove}
              gate={gate}
            />
          ))}
        </div>
      )}

      {/* Infinite-scroll sentinel (view mode only) */}
      {!editMode && (
        <div ref={loaderRef} className="flex justify-center py-4">
          {loadingMore && <span className="text-sm text-tertiary">Loading more</span>}
          {!hasMore && items.length > 0 && <span className="text-sm text-tertiary">No more items</span>}
        </div>
      )}
    </div>
  );
}
